import type { AccountScope, LogSet as LogSetT } from '@heraion/shared';

import { asyncStorageResourceCache } from './state/asyncListCache';

// #360: the sets of the recent window, kept on disk per exercise, so «Build
// today's session» has history to read with no network.
//
// ⭐ What it stores is SETS, not a load. The raw LogSets of the last window the
// server served (`/api/sessions?include=sets&limit=50`), grouped by slug, in
// the shape `loadTodayHistorySourced` already feeds through `fromLogSet`. The anchor
// (`todayHistoryFromSets`, TODAY_ANCHOR_WEEKS) stays the only rule that turns
// them into reps and kg: a stored «last load» would be a second rule, and it
// would drift from the first one the day the anchor changes.
//
// Sibling of bestE1rmFallback.ts, same writer (`ensureRecentWorkoutsCached`),
// same three cases: a failed fetch leaves the disk alone, a definitive empty
// clears it, a served window replaces it. Every save reaches it through the
// existing post-save path: `invalidateEntryHistoryCache`, then the next
// window fetch writes the new truth.

type Snapshot = {
  /** When the window was served, ISO. Its own field, as in bestE1rmFallback:
   *  `read()` does not surface the adapter's `cachedAt`. */
  savedAt: string;
  setsBySlug: Record<string, LogSetT[]>;
};

export type LastSetsSnapshot = {
  savedAtMs: number;
  setsBySlug: ReadonlyMap<string, readonly LogSetT[]>;
};

const cache = asyncStorageResourceCache<Snapshot>('logger/lastSetsBySlug');

/** The snapshot, or null when there is none or it cannot be read. A missing or
 *  garbled date fails closed: the reader cannot judge its age. */
export async function readLastSetsSnapshot(): Promise<LastSetsSnapshot | null> {
  const snap = await cache.read();
  if (snap === null || typeof snap !== 'object') return null;
  const savedAtMs = Date.parse(snap.savedAt);
  const raw = snap.setsBySlug;
  if (!Number.isFinite(savedAtMs) || raw === null || typeof raw !== 'object') return null;
  const setsBySlug = new Map<string, readonly LogSetT[]>();
  for (const [slug, sets] of Object.entries(raw)) {
    if (Array.isArray(sets) && sets.length > 0) setsBySlug.set(slug, sets);
  }
  return { savedAtMs, setsBySlug };
}

/** Retire the snapshot: the server answered, and there are no recent
 *  sessions. A failed fetch must not get here (bestE1rmFallback.ts). */
export async function clearLastSetsSnapshot(nowMs: number, scope: AccountScope): Promise<void> {
  await cache.write({ savedAt: new Date(nowMs).toISOString(), setsBySlug: {} }, scope);
}

/** Write the served window's sets. Best-effort, like the adapter. An empty
 *  list writes nothing: it is what a failed fetch looks like. */
export async function persistLastSetsSnapshot(
  sets: readonly LogSetT[],
  nowMs: number,
  /** The account the read was for (#398): this store is written unread. */
  scope: AccountScope,
): Promise<void> {
  if (sets.length === 0) return;
  const setsBySlug: Record<string, LogSetT[]> = {};
  for (const s of sets) {
    const list = setsBySlug[s.exerciseSlug];
    if (list) list.push(s);
    else setsBySlug[s.exerciseSlug] = [s];
  }
  await cache.write({ savedAt: new Date(nowMs).toISOString(), setsBySlug }, scope);
}
